import React, { useState, useRef } from 'react';
import { Upload, X, Image, FileArchive } from 'lucide-react';
import { eventsAPI } from '../api';

function UnifiedUpload({ eventId, onClose, onUploadComplete, theme }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    // Filter for valid files (images and zip)
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isZip = file.name.endsWith('.zip') || file.type === 'application/zip';
      return isImage || isZip;
    });

    if (validFiles.length === 0) {
      alert('Please select image files or zip files');
      return;
    }

    setSelectedFiles(validFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress({ current: 0, total: 0 });

      // Check if we have a zip file
      const zipFile = selectedFiles.find(f => f.name.endsWith('.zip'));
      
      if (zipFile) {
        // Upload zip file
        const formData = new FormData();
        formData.append('zipFile', zipFile);
        
        const response = await eventsAPI.uploadZipPhotos(eventId, formData, (progress) => {
          setUploadProgress(progress);
        });
        
        alert(`Successfully uploaded ${response.data.photosUploaded} photos from zip!`);
      } else {
        // Upload individual photos
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('photos', file);
        });
        
        const response = await eventsAPI.uploadPhotos(eventId, formData);
        alert(`Successfully uploaded ${selectedFiles.length} photo(s)!`);
      }
      
      onUploadComplete();
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      alert(`Failed to upload: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  return (
    <div className="modal-overlay" onClick={() => !uploading && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: theme === 'dark' ? '#fff' : '#000' }}>Upload Photos</h2>
          {!uploading && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} color={theme === 'dark' ? '#fff' : '#000'} />
            </button>
          )}
        </div>
        
        {!uploading ? (
          <div>
            <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '20px' }}>
              Upload individual photos or a zip file containing multiple photos
            </p>
            
            {/* Drag and drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? '#F97316' : (theme === 'dark' ? '#4b5563' : '#d1d5db')}`,
                borderRadius: '12px',
                padding: '40px',
                textAlign: 'center',
                background: isDragging ? (theme === 'dark' ? '#1f2937' : '#fff7ed') : (theme === 'dark' ? '#1f2937' : '#f9fafb'),
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '20px'
              }}
            >
              <Upload size={48} color={theme === 'dark' ? '#9ca3af' : '#6b7280'} style={{ margin: '0 auto 16px' }} />
              <p style={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: '500', marginBottom: '8px' }}>
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '14px' }}>
                or click to browse
              </p>
              <p style={{ color: theme === 'dark' ? '#6b7280' : '#9ca3af', fontSize: '12px', marginTop: '12px' }}>
                Supports: JPG, PNG, GIF, WEBP, HEIC, ZIP
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.zip"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: theme === 'dark' ? '#fff' : '#000', fontSize: '16px', marginBottom: '12px' }}>
                  Selected Files ({selectedFiles.length})
                </h3>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        background: theme === 'dark' ? '#374151' : '#f3f4f6',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {file.name.endsWith('.zip') ? (
                          <FileArchive size={20} color={theme === 'dark' ? '#9ca3af' : '#6b7280'} style={{ marginRight: '12px' }} />
                        ) : (
                          <Image size={20} color={theme === 'dark' ? '#9ca3af' : '#6b7280'} style={{ marginRight: '12px' }} />
                        )}
                        <div style={{ flex: 1 }}>
                          <p style={{ color: theme === 'dark' ? '#fff' : '#000', fontSize: '14px', margin: 0 }}>
                            {file.name}
                          </p>
                          <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '12px', margin: 0 }}>
                            {formatBytes(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        <X size={18} color={theme === 'dark' ? '#9ca3af' : '#6b7280'} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload button */}
            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0}
              style={{
                width: '100%',
                padding: '14px',
                background: selectedFiles.length === 0 ? '#9ca3af' : '#F97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s'
              }}
            >
              Upload {selectedFiles.length} {selectedFiles.length === 1 ? 'File' : 'Files'}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                border: '4px solid #f3f4f6', 
                borderTop: '4px solid #F97316',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
            </div>
            <p style={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: '500', marginBottom: '10px' }}>
              Uploading...
            </p>
            {uploadProgress.total > 0 && (
              <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                {(() => {
                  const percent = Math.round((uploadProgress.current / uploadProgress.total) * 100);
                  return `${formatBytes(uploadProgress.current)} of ${formatBytes(uploadProgress.total)} (${percent}%)`;
                })()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UnifiedUpload;
